import { useEffect, useState } from 'react';
import { LAPS, SEGMENTS, SPIN_MS, WHEEL, WHEEL_SIZE, type BigSixState, type Segment } from '../../engine/games/bigSix';
import { money } from '../../format';
import { Burst, Controls, secondChanceFrame, Stage, StatusLine, useAnimClock, useBurst, useLuckMoment, type TableProps } from './Frame';

/** A ★ second chance: the wheel first spins to the losing segment (the
 *  engine's frames last about this long), then luck, then the real spin. */
const LUCK_SPIN_MS = 1700;
const LUCK_AT_MS = 1850;
const LUCK_TOTAL_MS = 2250;
const ORDER: Segment[] = ['1', '2', '5', '10', '20', 'joker', 'logo']; // the engine's cycle order

const label = (id: Segment) => SEGMENTS.find((s) => s.id === id)!.label;
const ease = (t: number) => 1 - (1 - Math.min(1, Math.max(0, t))) ** 3;

const STEP = 360 / WHEEL_SIZE;
/** Short labels that fit a wedge. */
const WEDGE: Record<Segment, string> = { "1": "$1", "2": "$2", "5": "$5", "10": "$10", "20": "$20", joker: "JOKER", logo: "★" };

/** The carnival money wheel, seen from the front: only its top shows above
 *  the table. `pos` is a (fractional) wheel position; the wedge at `pos`
 *  sits under the clacker at the top. */
function Wheel({ pos, landed, spinning }: { pos: number; landed: boolean; spinning: boolean }) {
  const at = ((Math.round(pos) % WHEEL_SIZE) + WHEEL_SIZE) % WHEEL_SIZE;
  const frac = pos - Math.floor(pos);
  // The clacker flicks as each peg (between wedges) passes under it.
  const flick = spinning ? Math.max(0, 1 - Math.abs(frac - 0.5) / 0.22) * 28 : 0;
  const r0 = 30;
  const r1 = 92;
  const wedge = (i: number) => {
    const a0 = ((-i - 0.5) * STEP * Math.PI) / 180;
    const a1 = ((-i + 0.5) * STEP * Math.PI) / 180;
    const p = (r: number, a: number) => `${(r * Math.sin(a)).toFixed(2)},${(-r * Math.cos(a)).toFixed(2)}`;
    return `M${p(r0, a0)} L${p(r1, a0)} A${r1},${r1} 0 0 1 ${p(r1, a1)} L${p(r0, a1)} A${r0},${r0} 0 0 0 ${p(r0, a0)} Z`;
  };
  return (
    <svg className={`carnival${spinning ? " spinning" : ""}`} viewBox="-104 -108 208 134" role="img" aria-label={landed ? `Landed on ${label(WHEEL[at]!)}` : "The wheel"}>
      <circle r="104" className="wheel-rim" />
      {Array.from({ length: 18 }, (_, i) => (
        <circle key={i} transform={`rotate(${i * 20}) translate(0 -100)`} r="2.2" className={`wheel-bulb${i % 2 ? " odd" : ""}`} />
      ))}
      <g transform={`rotate(${(pos * STEP).toFixed(3)})`}>
        {WHEEL.map((seg, i) => (
          <g key={i}>
            <path d={wedge(i)} className={`wedge seg-${seg}${landed && i === at ? " landed" : ""}`} />
            <text transform={`rotate(${-i * STEP}) translate(0 -61) rotate(-90)`} className={`wedge-label seg-${seg}`} textAnchor="middle" dominantBaseline="central">{WEDGE[seg]}</text>
          </g>
        ))}
        {WHEEL.map((_, i) => (
          <circle key={i} transform={`rotate(${(-i + 0.5) * STEP}) translate(0 -95)`} r="1.6" className="peg" />
        ))}
      </g>
      <circle r={r0} className="wheel-hub" />
      {/* The counter the wheel stands behind: nothing below it is drawn. */}
      <rect x="-104" y="19" width="208" height="8" rx="2" className="wheel-ledge" />
      <text y="-9" className="hub-text" textAnchor="middle">BIG</text>
      <text y="7" className="hub-text" textAnchor="middle">SIX</text>
      <g transform={`translate(0 -104) rotate(${flick})`} className="clacker">
        <path d="M-4,-4 L4,-4 L2,16 L0,19 L-2,16 Z" />
        <circle r="3.5" />
      </g>
    </svg>
  );
}

export function BigSixTable({ snapshot, actions }: TableProps) {
  const run = snapshot.run!;
  const state = run.tables.bigsix?.state as BigSixState | undefined;
  const anim = snapshot.anim;
  const luck = !!secondChanceFrame(snapshot);
  const elapsed = useAnimClock(snapshot, actions, luck ? LUCK_TOTAL_MS : 0);
  const [burst, fading, setBurst] = useBurst();
  useLuckMoment(snapshot, elapsed, luck ? LUCK_AT_MS : null, () => ({ what: 'A losing spin spins again.', saved: !!state?.lucky }), setBurst);

  // The engine moves the wheel on its tick; redraw smoothly in between.
  const spinning = state?.phase === 'spinning';
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!spinning) return;
    const t = setInterval(() => setNow(Date.now()), 40);
    return () => clearInterval(t);
  }, [spinning]);

  let pos = state ? state.from : 0;
  if (state && anim && luck) {
    // The losing first spin, ending on the segment the engine saved as `from`.
    pos = state.from - (WHEEL_SIZE * LAPS + 17) * (1 - ease(elapsed / LUCK_SPIN_MS));
  } else if (state && spinning) {
    const travel = WHEEL_SIZE * LAPS + ((state.stop - state.from + WHEEL_SIZE) % WHEEL_SIZE);
    pos = state.from + travel * ease((now - state.startedAt) / SPIN_MS);
  }

  const bet = state?.bet ?? '1';
  const pick = (seg: Segment) => {
    if (!state || spinning || anim) return;
    const steps = (ORDER.indexOf(seg) - ORDER.indexOf(bet) + ORDER.length) % ORDER.length;
    for (let i = 0; i < steps; i++) actions.tableOption('segment');
  };

  let result: string | undefined;
  let tone: 'win' | 'loss' | '' = '';
  if (state?.phase === 'done' && state.last && !anim) {
    const net = state.returned - state.lastStake;
    result = `Last spin: ${label(state.last)} · your ${label(state.lastBet)} bet ${net > 0 ? `${state.lucky ? '★ ' : ''}+${money(net)}` : `−${money(state.lastStake)}`}`;
    tone = net > 0 ? 'win' : 'loss';
  }

  return (
    <>
      <Stage className="bigsix-stage" onClick={anim ? actions.skip : undefined}>
        <Burst burst={burst} fading={fading} pending={false} />
        <Wheel pos={pos} landed={state?.phase === 'done' && !anim} spinning={spinning || (!!anim && luck)} />
        {/* Always there (empty before the first spin), so the bets below
            never shift down when the first result arrives. */}
        <div className="history seg-history" aria-label="Last spins">
          {(state?.history ?? []).slice(0, 7).map((s, i) => <span key={i} className={`seg-chip seg-${s}`}>{label(s)}</span>)}
        </div>
        <div className="bac-spots-label">Your bet</div>
        <div className="seg-bets" role="radiogroup" aria-label="Your bet">
          {SEGMENTS.map((s) => (
            <button key={s.id} role="radio" aria-checked={bet === s.id} className={`seg-bet seg-${s.id}${bet === s.id ? ' on' : ''}`} onClick={() => pick(s.id)} disabled={spinning || !!anim}>
              <span className="seg-name">{s.label}</span>
              <small>{s.pays}:1 · {s.count}/{WHEEL_SIZE}</small>
            </button>
          ))}
        </div>
        <StatusLine snapshot={snapshot} result={result} tone={tone} />
      </Stage>
      <Controls snapshot={snapshot} actions={actions} hide={['segment', 'watch']} onAct={() => setBurst(null)} />
    </>
  );
}
